// ─────────────────────────────────────────────────────────────────────────────
// V2Clients — /dashboard/manageclients in the v2 design.
// Master–detail (macOS Contacts): alphabetical list with a selection pill that
// glides between rows; detail pane with contact info + customer-portal controls.
// Phone: list → detail opens as a bottom sheet.
// Endpoints are v1's: /api/clients/{fetchallclients, addclient, updateclient/:id,
// deleteclient/:id, :id/portal-status, :id/enable-portal, :id/disable-portal}.
// Unlike v1, server validation errors are surfaced instead of reported as success.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import clientContext from "../context/clients/clientContext";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { Sheet, Skel, useMedia } from "./primitives";
import { Spring } from "./spring";

const BASE = `${API_BASE}/api`;
const heads = () => ({ "Content-Type": "application/json", "auth-token": localStorage.getItem("token") });
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const initials = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const portalUrlFor = (token) => (token ? `${window.location.origin}/portal/${token}` : "");
const waNumber = (p = "") => {
  const d = p.replace(/[^0-9]/g, "");
  if (d.length < 10) return null;
  return d.startsWith("91") && d.length > 10 ? d : `91${d.slice(-10)}`;
};

function Avatar({ name, size = 40 }) {
  return <span className="v2-avatar v2-avatar--client" style={{ width: size, height: size, fontSize: size * 0.36 }}>{initials(name)}</span>;
}

// ══════════════════════════════════════════════════════════════════════════════
export default function V2Clients({ showAlert }) {
  usePageTitle("Clients");
  const navigate = useNavigate();
  const { clients, fetchAllClients } = useContext(clientContext);
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("clients", "write");
  const isNarrow = useMedia("(max-width: 900px)");

  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [form, setForm] = useState(null);          // null | { mode: "add"|"edit", client? }
  const [removing, setRemoving] = useState(null);  // client
  const addBtn = useRef(null);

  useEffect(() => { fetchAllClients().finally(() => setLoading(false)); }, []); // eslint-disable-line

  const list = useMemo(() => (Array.isArray(clients) ? clients.filter((c) => c && c.clientName) : []), [clients]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const arr = s ? list.filter((c) =>
      c.clientName.toLowerCase().includes(s) ||
      c.clientGSTIN?.toLowerCase().includes(s) ||
      c.clientEmail?.toLowerCase().includes(s) ||
      c.clientPhone?.toLowerCase().includes(s)) : list;
    return [...arr].sort((a, b) => a.clientName.localeCompare(b.clientName, "en", { sensitivity: "base" }));
  }, [list, q]);

  // Group A–Z (# for non-letters)
  const groups = useMemo(() => {
    const m = new Map();
    filtered.forEach((c) => {
      const ch = c.clientName.trim()[0]?.toUpperCase() || "#";
      const k = /[A-Z]/.test(ch) ? ch : "#";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(c);
    });
    return [...m.entries()];
  }, [filtered]);

  // Keep a valid selection on desktop
  useEffect(() => {
    if (isNarrow) return;
    if (!filtered.length) { setSelectedId(null); return; }
    if (!filtered.some((c) => c._id === selectedId)) setSelectedId(filtered[0]._id);
  }, [filtered, selectedId, isNarrow]);

  const selected = list.find((c) => c._id === selectedId) || null;
  const portalCount = list.filter((c) => c.portalEnabled).length;

  const select = (c) => {
    setSelectedId(c._id);
    if (isNarrow) setMobileOpen(true);
  };

  // ── Selection pill that glides between rows ────────────────────────────────
  const listRef = useRef(null);
  const pillRef = useRef(null);
  const rowRefs = useRef({});
  const sy = useRef(null);
  const sh = useRef(null);
  useEffect(() => {
    const apply = () => {
      if (!pillRef.current) return;
      pillRef.current.style.transform = `translateY(${sy.current.value}px)`;
      pillRef.current.style.height = `${sh.current.value}px`;
    };
    sy.current = new Spring(0, { response: 0.34, onUpdate: apply });
    sh.current = new Spring(0, { response: 0.34, onUpdate: apply });
    return () => { sy.current.stop(); sh.current.stop(); };
  }, []);
  const placed = useRef(false);
  const placePill = useCallback((animate) => {
    const row = rowRefs.current[selectedId];
    if (!pillRef.current) return;
    if (!row || isNarrow) { pillRef.current.style.opacity = "0"; placed.current = false; return; }
    pillRef.current.style.opacity = "1";
    if (animate && placed.current) { sy.current.to(row.offsetTop); sh.current.to(row.offsetHeight); }
    else { sy.current.set(row.offsetTop); sh.current.set(row.offsetHeight); }
    placed.current = true;
  }, [selectedId, isNarrow]);
  useLayoutEffect(() => { placePill(true); }, [placePill]);
  useLayoutEffect(() => { placePill(false); }, [q, groups.length]); // eslint-disable-line

  // Arrow-key navigation through the list
  const onListKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const i = filtered.findIndex((c) => c._id === selectedId);
    const next = filtered[Math.max(0, Math.min(filtered.length - 1, i + (e.key === "ArrowDown" ? 1 : -1)))];
    if (next) { setSelectedId(next._id); rowRefs.current[next._id]?.focus(); }
  };

  const afterSave = async (msg) => {
    await fetchAllClients();
    showAlert?.(msg, "success");
  };

  const detail = selected && (
    <ClientDetail
      key={selected._id}
      client={selected}
      canWrite={canWrite}
      onEdit={() => setForm({ mode: "edit", client: selected })}
      onRemove={() => setRemoving(selected)}
      onPortalChange={fetchAllClients}
      navigate={navigate}
      showAlert={showAlert}
    />
  );

  return (
    <div className="v2p">
      <header className="v2p-head">
        <div>
          <h1 className="v2p-title">Clients</h1>
          <p className="v2p-sub">
            {loading ? "Loading…" : `${list.length} client${list.length === 1 ? "" : "s"} · ${portalCount} with portal access`}
          </p>
        </div>
        {canWrite && (
          <button ref={addBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setForm({ mode: "add" })}>
            <Glyph name="plus" size={17} stroke={2.2} /> New client
          </button>
        )}
      </header>

      <div className="v2c-split">
        {/* ── List ─────────────────────────────────────────────────────── */}
        <section className="v2-card v2c-listcard" aria-label="Client list">
          <div className="v2-field--search v2c-search">
            <Glyph name="search" size={17} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, GSTIN, email, phone"
              aria-label="Search clients" />
            {q && <button type="button" className="v2c-clear" aria-label="Clear search" onClick={() => setQ("")}><Glyph name="close" size={13} stroke={2.2} /></button>}
          </div>

          {loading ? (
            <div className="v2-list" style={{ marginTop: 12 }}>{[0, 1, 2, 3, 4, 5].map((i) => <Skel key={i} h={54} r={16} />)}</div>
          ) : filtered.length === 0 ? (
            <div className="v2c-empty">
              <Duo name="clients" size={44} />
              <strong>{q ? "No matches" : "No clients yet"}</strong>
              <span>{q ? `Nothing matches “${q}”.` : "Add your first client to start invoicing."}</span>
              {!q && canWrite && <button type="button" className="v2-btn v2-btn--sm v2-btn--primary" onClick={() => setForm({ mode: "add" })}>New client</button>}
            </div>
          ) : (
            <div ref={listRef} className="v2c-list" role="listbox" aria-label="Clients" onKeyDown={onListKey}>
              <span ref={pillRef} className="v2c-pill" aria-hidden="true" />
              {groups.map(([letter, items]) => (
                <div key={letter} className="v2c-group">
                  <div className="v2c-letter">{letter}</div>
                  {items.map((c) => (
                    <button key={c._id} ref={(el) => (rowRefs.current[c._id] = el)} type="button" role="option"
                      aria-selected={c._id === selectedId}
                      className={`v2c-row${c._id === selectedId && !isNarrow ? " is-sel" : ""}`}
                      onClick={() => select(c)}>
                      <Avatar name={c.clientName} size={38} />
                      <span className="v2-row-main">
                        <span className="v2-row-title">{c.clientName}</span>
                        <span className="v2-row-sub v2-mono">{c.clientGSTIN || "No GSTIN"}</span>
                      </span>
                      {c.portalEnabled && <span className="v2c-portal-dot" title="Portal active"><Glyph name="arrowUpRight" size={12} stroke={2.2} /></span>}
                      {isNarrow && <Glyph name="chevronRight" size={16} className="v2-inset-chev" />}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="v2c-listfoot">{q ? `${filtered.length} of ${list.length}` : `${list.length} clients`}</div>
          )}
        </section>

        {/* ── Detail (desktop) ─────────────────────────────────────────── */}
        {!isNarrow && (
          <section className="v2-card v2c-detailcard" aria-label="Client details">
            {loading ? <Skel h={320} r={20} /> : detail || (
              <div className="v2c-empty"><Duo name="clients" size={44} /><span>Select a client to see their details.</span></div>
            )}
          </section>
        )}
      </div>

      {/* Phone: detail as a sheet */}
      {isNarrow && (
        <Sheet open={mobileOpen && !!selected} onClose={() => setMobileOpen(false)} title="Client">
          {detail}
        </Sheet>
      )}

      <ClientForm state={form} originRef={addBtn} onClose={() => setForm(null)}
        onSaved={async (msg, id) => { setForm(null); await afterSave(msg); if (id) setSelectedId(id); }} />

      <RemoveSheet client={removing} onClose={() => setRemoving(null)}
        onDone={async (name) => { setRemoving(null); setMobileOpen(false); await afterSave(`${name} removed`); }} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Detail pane
// ══════════════════════════════════════════════════════════════════════════════
function ClientDetail({ client, canWrite, onEdit, onRemove, onPortalChange, navigate, showAlert }) {
  const [copiedG, setCopiedG] = useState(false);
  const copyGstin = () => {
    navigator.clipboard?.writeText(client.clientGSTIN || "").then(() => {
      setCopiedG(true); setTimeout(() => setCopiedG(false), 1600);
    });
  };

  return (
    <div className="v2c-detail">
      <div className="v2c-hero">
        <Avatar name={client.clientName} size={72} />
        <div className="v2c-hero-text">
          <h2 className="v2c-name">{client.clientName}</h2>
          {client.clientGSTIN ? (
            <button type="button" className="v2c-gstin" onClick={copyGstin} title="Copy GSTIN">
              <span className="v2-mono">{client.clientGSTIN}</span>
              <Glyph name={copiedG ? "check" : "note"} size={14} stroke={2} />
              <span className="v2c-gstin-tip">{copiedG ? "Copied" : "Copy"}</span>
            </button>
          ) : <span className="v2-row-sub">No GSTIN on file</span>}
        </div>
        {canWrite && (
          <div className="v2c-hero-actions">
            <button type="button" className="v2-btn v2-btn--sm" onClick={onEdit}><Glyph name="note" size={15} /> Edit</button>
          </div>
        )}
      </div>

      <div className="v2c-quick">
        <button type="button" className="v2c-quick-btn" onClick={() => navigate("/dashboard/createinvoice")}>
          <Duo name="invoice" size={26} /><span>Invoice</span>
        </button>
        <button type="button" className="v2c-quick-btn" onClick={() => navigate("/dashboard/ledgerpage")}>
          <Duo name="ledger" size={26} /><span>Ledger</span>
        </button>
        <a className={`v2c-quick-btn${client.clientEmail ? "" : " is-off"}`} href={client.clientEmail ? `mailto:${client.clientEmail}` : undefined} aria-disabled={!client.clientEmail}>
          <Duo name="mail" size={26} /><span>Email</span>
        </a>
        <a className={`v2c-quick-btn${client.clientPhone ? "" : " is-off"}`} href={client.clientPhone ? `tel:${client.clientPhone}` : undefined} aria-disabled={!client.clientPhone}>
          <Duo name="phone" size={26} /><span>Call</span>
        </a>
      </div>

      <div className="v2c-section-title">Contact</div>
      <div className="v2-inset">
        <InfoRow glyph="mail" label="Email" value={client.clientEmail} href={client.clientEmail && `mailto:${client.clientEmail}`} note="Invoices are emailed here automatically" />
        <InfoRow glyph="phone" label="Phone" value={client.clientPhone} href={client.clientPhone && `tel:${client.clientPhone}`} />
        <InfoRow glyph="pin" label="Address" value={client.clientAddress} />
        {client.creditBalance > 0 && (
          <InfoRow glyph="cash" label="Advance / credit balance" value={`₹${Number(client.creditBalance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`} />
        )}
      </div>

      <div className="v2c-section-title">Customer portal</div>
      <PortalCard client={client} canWrite={canWrite} onChange={onPortalChange} showAlert={showAlert} />

      {canWrite && (
        <button type="button" className="v2c-danger-link" onClick={onRemove}>
          <Glyph name="close" size={14} stroke={2} /> Remove client
        </button>
      )}
    </div>
  );
}

function InfoRow({ glyph, label, value, href, note }) {
  const body = (
    <>
      <Duo name={glyph} size={24} />
      <span className="v2-row-main">
        <span className="v2-row-sub">{label}</span>
        <span className={`v2-row-title v2-wrap${value ? "" : " v2c-missing"}`}>{value || "Not added"}</span>
        {note && value && <span className="v2c-note">{note}</span>}
      </span>
      {href && <Glyph name="arrowUpRight" size={15} className="v2-inset-chev" />}
    </>
  );
  return href
    ? <a className="v2-inset-row v2c-info" href={href}>{body}</a>
    : <div className="v2-inset-row v2-inset-row--static v2c-info">{body}</div>;
}

// ── Portal controls ──────────────────────────────────────────────────────────
function PortalCard({ client, canWrite, onChange, showAlert }) {
  const [status, setStatus] = useState(null);
  const [working, setWorking] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setStatus(null); setConfirmOff(false); setErr("");
    fetch(`${BASE}/clients/${client._id}/portal-status`, { headers: heads() })
      .then((r) => r.json())
      .then((d) => alive && setStatus({ portalEnabled: !!d.portalEnabled, portalToken: d.portalToken || null }))
      .catch(() => alive && setStatus({ portalEnabled: false, portalToken: null }));
    return () => { alive = false; };
  }, [client._id]);

  const url = portalUrlFor(status?.portalToken);

  const call = async (path, okMsg) => {
    setWorking(true); setErr("");
    try {
      const res = await fetch(`${BASE}/clients/${client._id}/${path}`, { method: "POST", headers: heads() });
      const d = await res.json();
      if (!res.ok) { setErr(d.error || "Something went wrong."); return; }
      setStatus({ portalEnabled: !!d.portalEnabled, portalToken: d.portalToken || null });
      setConfirmOff(false);
      onChange?.();
      showAlert?.(okMsg, "success");
    } catch { setErr("Network error — please try again."); }
    finally { setWorking(false); }
  };

  const copy = () => navigator.clipboard?.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); });

  const share = async () => {
    const text = `Hello ${client.clientName}, you can view your invoices and account statement here:`;
    if (navigator.share) {
      try { await navigator.share({ title: "Your customer portal", text, url }); } catch { /* cancelled */ }
    } else copy();
  };
  const wa = waNumber(client.clientPhone);
  const waHref = wa && url ? `https://wa.me/${wa}?text=${encodeURIComponent(`Hello ${client.clientName}, you can view your invoices and account statement anytime here:\n${url}`)}` : null;

  if (!status) return <div className="v2c-portal"><Skel h={92} r={18} /></div>;

  if (!status.portalEnabled) {
    return (
      <div className="v2c-portal v2c-portal--off">
        <div className="v2c-portal-head">
          <Duo name="invoice" size={34} />
          <div>
            <strong>Give {client.clientName} a private statement</strong>
            <span>A secure link where they can see every invoice, download PDFs and check their balance — no login needed.</span>
          </div>
        </div>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
        {canWrite && (
          <button type="button" className="v2-btn v2-btn--primary v2c-portal-cta" disabled={working} onClick={() => call("enable-portal", "Portal link created")}>
            {working ? "Creating link…" : "Create portal link"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="v2c-portal">
      <div className="v2c-portal-live"><span className="v2c-live-dot" /> Live · anyone with this link can view</div>
      <div className="v2c-link">
        <span className="v2c-link-url" title={url}>{url.replace(/^https?:\/\//, "")}</span>
        <button type="button" className={`v2-btn v2-btn--sm${copied ? " is-done" : ""}`} onClick={copy}>
          <Glyph name={copied ? "check" : "note"} size={15} stroke={2} /> {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div className="v2c-portal-actions">
        <a className="v2-btn v2-btn--sm" href={`${url}?ui=v2`} target="_blank" rel="noreferrer"><Glyph name="arrowUpRight" size={15} /> Preview</a>
        {waHref && <a className="v2-btn v2-btn--sm" href={waHref} target="_blank" rel="noreferrer"><Glyph name="phone" size={15} /> WhatsApp</a>}
        <button type="button" className="v2-btn v2-btn--sm" onClick={share}><Glyph name="upload" size={15} /> Share</button>
      </div>
      {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      {canWrite && (confirmOff ? (
        <div className="v2c-confirm">
          <span>The current link will stop working immediately.</span>
          <div>
            <button type="button" className="v2-btn v2-btn--sm" onClick={() => setConfirmOff(false)}>Keep</button>
            <button type="button" className="v2-btn v2-btn--sm v2c-btn-danger" disabled={working} onClick={() => call("disable-portal", "Portal link turned off")}>
              {working ? "Turning off…" : "Turn off link"}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="v2c-danger-link v2c-danger-link--sm" onClick={() => setConfirmOff(true)}>Turn off portal link</button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Add / edit sheet — validates with the same rules the backend enforces
// (name ≥ 3, GSTIN ≥ 15, address ≥ 5) and surfaces server errors.
// ══════════════════════════════════════════════════════════════════════════════
const EMPTY = { clientName: "", clientGSTIN: "", clientAddress: "", clientEmail: "", clientPhone: "" };

function ClientForm({ state, originRef, onClose, onSaved }) {
  const [v, setV] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const keep = useRef(state);
  if (state) keep.current = state;
  const s = keep.current;

  useEffect(() => {
    if (!state) return;
    setErr(""); setTouched({});
    setV(state.mode === "edit" ? {
      clientName: state.client.clientName || "", clientGSTIN: state.client.clientGSTIN || "",
      clientAddress: state.client.clientAddress || "", clientEmail: state.client.clientEmail || "",
      clientPhone: state.client.clientPhone || "",
    } : EMPTY);
  }, [state]);

  const errors = {
    clientName: v.clientName.trim().length < 3 ? "At least 3 characters" : "",
    clientGSTIN: v.clientGSTIN.trim().length < 15 ? "GSTIN is 15 characters"
      : !GSTIN_RE.test(v.clientGSTIN.trim()) ? "" : "",
    clientAddress: v.clientAddress.trim().length < 5 ? "Add the billing address (5+ characters)" : "",
    clientEmail: v.clientEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.clientEmail) ? "Check the email address" : "",
  };
  const gstinLooksOff = v.clientGSTIN.trim().length === 15 && !GSTIN_RE.test(v.clientGSTIN.trim());
  const valid = !Object.values(errors).some(Boolean);

  const set = (k) => (e) => {
    let val = e.target.value;
    if (k === "clientGSTIN") val = val.toUpperCase().replace(/\s/g, "").slice(0, 15);
    setV((p) => ({ ...p, [k]: val }));
  };
  const blur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ clientName: true, clientGSTIN: true, clientAddress: true, clientEmail: true });
    if (!valid) return;
    setBusy(true); setErr("");
    const body = JSON.stringify({ ...v, clientName: v.clientName.trim(), clientGSTIN: v.clientGSTIN.trim(), clientAddress: v.clientAddress.trim() });
    try {
      const res = s.mode === "edit"
        ? await fetch(`${BASE}/clients/updateclient/${s.client._id}`, { method: "PUT", headers: heads(), body })
        : await fetch(`${BASE}/clients/addclient`, { method: "POST", headers: heads(), body });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(d.errors?.map((x) => x.msg).join(" · ") || d.error || "The server couldn't save this client.");
        return;
      }
      onSaved(s.mode === "edit" ? "Client updated" : "Client added", s.mode === "edit" ? s.client._id : d._id);
    } catch { setErr("Network error — nothing was saved."); }
    finally { setBusy(false); }
  };

  if (!s) return null;
  const F = ({ k, label, type = "text", placeholder, optional, hint, mono, autoComplete }) => (
    <label className="v2-field">
      <span className="v2-field-label">{label}{optional && <span className="v2-optional">optional</span>}</span>
      <input className={`v2-input${mono ? " v2-mono" : ""}${touched[k] && errors[k] ? " is-bad" : ""}`} type={type}
        value={v[k]} onChange={set(k)} onBlur={blur(k)} placeholder={placeholder} autoComplete={autoComplete} />
      {touched[k] && errors[k] ? <span className="v2-field-err">{errors[k]}</span> : hint ? <span className="v2-field-hint">{hint}</span> : null}
    </label>
  );

  return (
    <Sheet open={!!state} onClose={onClose} originRef={s.mode === "add" ? originRef : undefined} width={560}
      title={s.mode === "edit" ? "Edit client" : "New client"}
      subtitle={s.mode === "edit" ? s.client.clientName : "Saved to your client directory"}
      footer={<>
        <button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="submit" form="v2-client-form" className="v2-btn v2-btn--primary" disabled={busy}>
          {busy ? "Saving…" : s.mode === "edit" ? "Save changes" : "Add client"}
        </button>
      </>}>
      <form id="v2-client-form" className="v2-form" onSubmit={submit} noValidate>
        {F({ k: "clientName", label: "Business name", placeholder: "ABC Enterprises", autoComplete: "organization" })}
        <div className="v2-form-row">
          {F({ k: "clientGSTIN", label: "GSTIN", placeholder: "27AAPFU0939F1ZV", mono: true,
            hint: gstinLooksOff ? "That doesn't look like a standard GSTIN — double-check it" : "15 characters, auto-capitalised" })}
          {F({ k: "clientPhone", label: "Phone", type: "tel", placeholder: "+91 98765 43210", optional: true, autoComplete: "tel" })}
        </div>
        {F({ k: "clientAddress", label: "Billing address", placeholder: "Street, city, state, PIN", autoComplete: "street-address" })}
        {F({ k: "clientEmail", label: "Email", type: "email", placeholder: "accounts@example.com", optional: true,
          hint: "New invoices are emailed here automatically", autoComplete: "email" })}
        {err && <div className="v2-banner v2-banner--bad" role="alert"><Glyph name="alert" size={17} /> {err}</div>}
      </form>
    </Sheet>
  );
}

// ── Remove confirmation (destructive → explicit) ─────────────────────────────
function RemoveSheet({ client, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const keep = useRef(client);
  if (client) keep.current = client;
  const c = keep.current;
  useEffect(() => { if (client) setErr(""); }, [client]);
  if (!c) return null;
  const remove = async () => {
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${BASE}/clients/deleteclient/${c._id}`, { method: "DELETE", headers: heads() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(d.error || "Couldn't remove this client."); return; }
      onDone(c.clientName);
    } catch { setErr("Network error — nothing was changed."); }
    finally { setBusy(false); }
  };
  return (
    <Sheet open={!!client} onClose={onClose} title={`Remove ${c.clientName}?`} width={440}
      footer={<>
        <button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="v2-btn v2c-btn-danger" disabled={busy} onClick={remove}>{busy ? "Removing…" : "Remove client"}</button>
      </>}>
      <p className="v2c-remove-text">
        They'll disappear from your client list and pickers. Invoices you've already issued to them are not changed.
      </p>
      {err && <div className="v2-banner v2-banner--bad" role="alert"><Glyph name="alert" size={17} /> {err}</div>}
    </Sheet>
  );
}
